/**
 * The sea palette, both themes, from the web-head design handoff.
 *
 * Two rules the handoff is emphatic about, and they are the reason this file
 * exists rather than hexes in components:
 *
 * - **Ink is three solid tokens, not one colour at alpha.** Text is `ink`,
 *   `inkSoft` or `inkFaint` — never `ink` at 32%. Alpha ramps exist only for
 *   lines and hovers, and they are declared here too.
 * - **A status wears its own hue.** Never an alias of a role that means
 *   something else — `next` is not an assistant thing, `active` is not a
 *   favourite. The values are the app's (`PaperTokens`): authored for dark,
 *   and in light the app's `fill()` walk of the same hue to 4.6:1 on `bg`,
 *   with chroma reduced to stay in gamut.
 */

import { tagTones } from './tagPalette';
import type { TagColorId } from './tagPalette';

export type Theme = 'dark' | 'light';

export interface Palette {
  bg: string;
  bgCard: string;
  bgSheet: string;
  ink: string;
  inkSoft: string;
  inkFaint: string;
  accent: string;
  heart: string;
  assistant: string;

  statusActive: string;
  statusNeedsInput: string;
  statusNext: string;
  statusParked: string;
  statusDone: string;

  hair: string;
  rule: string;
  ruleStrong: string;
  rowHover: string;

  /** A card resting on the plane. Lighter than `bg` in light, where
   *  `bgCard` is darker and reads as lowered. */
  paper: string;
  /** Writing and windows opened over the plane. */
  sheet: string;
  stickyWarm: string;
  stickyGreen: string;
  /** The plane's dot grid. */
  dot: string;
  /** Laid over the plane behind a document sheet. */
  scrim: string;
  /** Accent as text or a glyph on `accentWash`, where plain accent fails
   *  contrast. */
  accentInk: string;
  accentWash: string;
  /** "waiting on you" as a line of text rather than a glyph. */
  needsInk: string;
  /** Shadows: `lift` for what rests on the plane, `liftBig` for what opens
   *  over it. */
  lift: string;
  liftBig: string;
  /** The soft shadow of a panel hung over a table. */
  shade: string;
}

const DARK = {
  bg: '#171F18',
  bgCard: '#232E25',
  bgSheet: '#0E130F',
  ink: '#F4EFE0',
  inkSoft: '#BEBAA6',
  inkFaint: '#858C7B',
  accent: '#319880',
  heart: '#FFC107',
  assistant: '#B49ED6',
} as const;

const LIGHT = {
  bg: '#E7E9DD',
  bgCard: '#D9DCCC',
  // Same value as `bg` in the handoff. Kept faithfully, but it means a menu
  // floating over content separates by its 1px rule alone — flagged in the
  // review, and the place to fix it when it proves to be a problem.
  bgSheet: '#E7E9DD',
  ink: '#1F2418',
  inkSoft: '#4E5544',
  inkFaint: '#858A78',
  accent: '#319880',
  // Deep amber, matching the app. Dark's heart is gold (OKLCH hue 85); this
  // was hue 40, so the same glyph read as a different colour between themes
  // rather than a darker one. Hue 65 is as close to that gold as paper allows
  // and clears 3:1 on bg and card.
  heart: '#AE6700',
  assistant: '#6B5B9E',
} as const;

export const THEMES: Record<Theme, Palette> = {
  dark: {
    ...DARK,
    statusActive: '#E0A93B',
    statusNeedsInput: '#DC9AA6',
    statusNext: '#9AA48F',
    statusParked: '#7C8478',
    statusDone: DARK.accent,
    hair: alpha(DARK.ink, 0.09),
    rule: alpha(DARK.ink, 0.1),
    ruleStrong: alpha(DARK.ink, 0.22),
    rowHover: alpha(DARK.ink, 0.045),
    paper: DARK.bgCard,
    sheet: '#1C251E',
    stickyWarm: '#3A3423',
    stickyGreen: '#1F3A2F',
    dot: alpha(DARK.ink, 0.065),
    scrim: alpha(DARK.bg, 0.62),
    accentInk: '#7BD3BA',
    accentWash: alpha(DARK.accent, 0.22),
    needsInk: '#E79BB4',
    lift: '0 1px 0 rgb(0 0 0 / 0.25), 0 12px 32px -12px rgb(0 0 0 / 0.65)',
    liftBig: '0 1px 0 rgb(0 0 0 / 0.25), 0 22px 60px -20px rgb(0 0 0 / 0.75)',
    shade: 'rgb(0 0 0 / 0.28)',
  },
  light: {
    ...LIGHT,
    statusActive: '#7D5800',
    statusNeedsInput: '#8E535F',
    statusNext: '#5F6855',
    statusParked: '#5A6156',
    statusDone: LIGHT.accent,
    hair: alpha(LIGHT.ink, 0.12),
    rule: alpha(LIGHT.ink, 0.14),
    ruleStrong: alpha(LIGHT.ink, 0.22),
    rowHover: alpha(LIGHT.ink, 0.05),
    paper: '#F3F4EC',
    sheet: '#F6F6F0',
    stickyWarm: '#F4E9C9',
    stickyGreen: '#DCEBDF',
    dot: alpha(LIGHT.ink, 0.075),
    scrim: alpha(LIGHT.bg, 0.55),
    accentInk: '#1F6B5A',
    accentWash: alpha(LIGHT.accent, 0.14),
    needsInk: '#8A3352',
    lift: `0 1px 0 ${alpha(LIGHT.ink, 0.04)}, 0 10px 30px -12px ${alpha(LIGHT.ink, 0.22)}`,
    liftBig: `0 1px 0 ${alpha(LIGHT.ink, 0.04)}, 0 18px 50px -18px ${alpha(LIGHT.ink, 0.3)}`,
    shade: 'rgb(31 36 24 / 0.12)',
  },
};

export function alpha(hex: string, a: number): string {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return `rgb(${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255} / ${a})`;
}

const srgbLin = (c: number) => {
  c /= 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const srgbEnc = (v: number) => {
  v = Math.min(1, Math.max(0, v));
  return v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
};

/** Gamma-encoded sRGB channels, 0–1. */
type Rgb = [number, number, number];

interface Oklch {
  l: number;
  c: number;
  /** Degrees. */
  h: number;
}

const hexOf = (rgb: Rgb): string =>
  `#${rgb
    .map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

/** A hex colour in OKLCH. */
export function oklchOf(hex: string): Oklch {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  const r = srgbLin((n >> 16) & 255);
  const g = srgbLin((n >> 8) & 255);
  const b = srgbLin(n & 255);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { l: L, c: Math.hypot(A, B), h: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360 };
}

/** OKLCH hue of a hex colour, in degrees. */
export function hueOf(hex: string): number {
  return oklchOf(hex).h;
}

/** Linear sRGB channels, unclamped: a channel outside 0–1 is out of gamut. */
function linearOfOklch({ l, c, h }: Oklch): Rgb {
  const A = c * Math.cos((h * Math.PI) / 180);
  const B = c * Math.sin((h * Math.PI) / 180);
  const l3 = (l + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m3 = (l - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s3 = (l - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3,
    -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3,
    -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3,
  ];
}

/** Back to sRGB, a channel outside the gamut clamped. */
function rgbOfOklch(o: Oklch): Rgb {
  return linearOfOklch(o).map(srgbEnc) as Rgb;
}

const inGamut = (rgb: Rgb) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4);

/** [o] with its chroma given up until it fits in sRGB, hue and lightness
 *  kept — the app's `Oklch.toColor`. */
function fittedOklch(o: Oklch): Oklch {
  if (inGamut(linearOfOklch(o))) return o;
  let lo = 0;
  let hi = o.c;
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut(linearOfOklch({ ...o, c: mid }))) lo = mid;
    else hi = mid;
  }
  return { ...o, c: lo };
}

export interface Container {
  fill: string;
  border: string;
}

/**
 * An arc drawn as a card in its folder's colour: the band, bordered by the
 * edge — how an arc sits on a canvas, a lane board or the arc plane.
 */
export function arcTint(theme: Theme, hue: TagColorId): Container {
  const tones = tagTones(theme, hue);
  return { fill: tones.band, border: tones.edge };
}

/** A pill filled with a folder's colour, bordered by its edge. */
export function chipTint(theme: Theme, hue: TagColorId): Container {
  const tones = tagTones(theme, hue);
  return { fill: tones.chip, border: tones.edge };
}

/** What a canvas stands on: a nook's canvas and a nook card's miniature
 *  alike. */
export interface CanvasGround {
  /** The untinted page whatever the nook's tag, so a card in that tag never
   *  melts into it. */
  fill: string;
  /** The one place a canvas carries its colour: the folder's mark at 30%. */
  dot: string;
}

export function canvasGround(theme: Theme, hue: TagColorId | null): CanvasGround {
  const palette = THEMES[theme];
  return { fill: palette.bg, dot: hue ? alpha(tagTones(theme, hue).mark, 0.3) : palette.dot };
}

/** What a folder's mark — its icon — is drawn in: its mark tone, or the soft
 *  ink for a folder with no colour. */
export function markInk(theme: Theme, hue: TagColorId | null): string {
  return hue ? tagTones(theme, hue).mark : 'var(--inkSoft)';
}

/** How far each step of the tone ladder lifts `bg`'s OKLCH lightness — the
 *  app's `SurfaceKit.tone`, brighter upward in both themes. */
const TONE_LIFT = [0.012, 0.03, 0.055, 0.085] as const;

const bakedTones = new Map<string, string>();

/**
 * A step of the tone ladder, computed from `bg` rather than picked. Step 1 is
 * a recording's or note's card base; step 3 lights an untagged nook's sign.
 */
export function tone(theme: Theme, step: 0 | 1 | 2 | 3): string {
  const key = `${theme}:${step}`;
  const known = bakedTones.get(key);
  if (known) return known;
  const bg = oklchOf(THEMES[theme].bg);
  const value = hexOf(rgbOfOklch(fittedOklch({ ...bg, l: Math.min(1, bg.l + TONE_LIFT[step]) })));
  bakedTones.set(key, value);
  return value;
}

/**
 * A recording's or a note's card, and a small folder: its folder's card tone,
 * which sits above the page, or the page lifted one step with no colour.
 */
export function cardFill(theme: Theme, hue: TagColorId | null): string {
  return hue ? tagTones(theme, hue).card : tone(theme, 1);
}

/**
 * How wide a recording's or a note's card is, wherever it is drawn — the
 * Thoughts wall, a nook's panel, its canvas — so an item's face falls into
 * the same lines everywhere and is known again by its shape. `--cardWidth`
 * in CSS.
 */
export const CARD_WIDTH = 380;

/** Writes the palette onto the document as custom properties, so components
 *  say `var(--ink)` and never a hex; the card width and the tone ladder
 *  (`--tone0` to `--tone3`) ride along. */
export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  const palette = THEMES[theme];
  for (const [name, value] of Object.entries(palette)) {
    root.style.setProperty(`--${name}`, value);
  }
  root.style.setProperty('--cardWidth', `${CARD_WIDTH}px`);
  for (const step of [0, 1, 2, 3] as const) root.style.setProperty(`--tone${step}`, tone(theme, step));
  root.dataset['theme'] = theme;
  root.style.colorScheme = theme;
}
